import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Plus,
  Trash2,
  FileSpreadsheet,
  Loader2,
  Coffee,
  Save,
  Download,
  Calendar as CalendarIcon,
} from "lucide-react";

const fmt = (n) => {
  if (!n && n !== 0) return "";
  return new Intl.NumberFormat("vi-VN").format(Math.round(n));
};

function formatDateVN(d) {
  if (!d || d.length < 10) return d;
  return `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`;
}

export default function DoanhThuPippyTab() {
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 7) + "-01";
  const [start, setStart] = useState(monthStart);
  const [end, setEnd] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingKiot, setEditingKiot] = useState(null); // {date, total_kiot, note}
  const [newEntry, setNewEntry] = useState({
    date: today,
    item_name: "",
    unit_price: 0,
    quantity: 1,
    total: 0,
    total_overridden: false,
    note: "",
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/doanh-thu-pippy?start=${start}&end=${end}`);
      setData(d);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi tải dữ liệu");
    } finally {
      setLoading(false);
    }
  }, [start, end]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddEntry = async () => {
    if (!newEntry.item_name.trim()) {
      toast.error("Vui lòng nhập tên hàng hoá");
      return;
    }
    try {
      const up = parseInt(newEntry.unit_price) || 0;
      const qty = parseInt(newEntry.quantity) || 0;
      // Nếu CEO không override total → mặc định = up * qty (chỉ dialog thủ công, không phải import)
      const total = newEntry.total_overridden
        ? (parseInt(newEntry.total) || 0)
        : up * qty;
      await api.post("/doanh-thu-pippy/entries", {
        date: newEntry.date,
        item_name: newEntry.item_name,
        unit_price: up,
        quantity: qty,
        total,
        note: newEntry.note,
      });
      toast.success("Đã thêm dòng");
      setShowAddDialog(false);
      setNewEntry({ date: today, item_name: "", unit_price: 0, quantity: 1, total: 0, total_overridden: false, note: "" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi thêm");
    }
  };

  const handleDeleteEntry = async (id, item) => {
    if (!window.confirm(`Xoá dòng "${item}"?`)) return;
    try {
      await api.delete(`/doanh-thu-pippy/entries/${id}`);
      toast.success("Đã xoá");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi xoá");
    }
  };

  const handleSaveKiotTotal = async () => {
    if (!editingKiot) return;
    try {
      await api.post("/doanh-thu-pippy/kiot-total", {
        date: editingKiot.date,
        total_kiot: parseInt(editingKiot.total_kiot) || 0,
        note: editingKiot.note || "",
      });
      toast.success("Đã cập nhật tổng tiền kiot");
      setEditingKiot(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi lưu");
    }
  };

  const handleExportExcel = async () => {
    try {
      const resp = await api.get(`/doanh-thu-pippy/export?start=${start}&end=${end}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `doanh_thu_pippy_${start}_${end}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Đã xuất Excel");
    } catch (err) {
      toast.error("Lỗi xuất file");
    }
  };

  const groups = data?.groups || [];
  const grandSoKetVong = data?.grand_total_so_ket_vong || 0;
  const grandKiot = data?.grand_total_kiot || 0;
  const grandLech = data?.grand_lech || 0;

  return (
    <div className="space-y-3" data-testid="doanh-thu-pippy-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm font-bold text-slate-200 flex items-center gap-2">
          <Coffee className="w-4 h-4 text-amber-400" /> Doanh thu Pippy
        </h2>
        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            className="h-8 bg-[#0f172a] border-[#334155] text-slate-200 text-xs w-[130px]"
            data-testid="pippy-date-start"
          />
          <span className="text-slate-500 text-xs">→</span>
          <Input
            type="date"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            className="h-8 bg-[#0f172a] border-[#334155] text-slate-200 text-xs w-[130px]"
            data-testid="pippy-date-end"
          />
          <Button
            onClick={() => setShowAddDialog(true)}
            size="sm"
            className="h-8 bg-emerald-500 hover:bg-emerald-400 text-white text-xs"
            data-testid="pippy-add-btn"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Thêm dòng
          </Button>
          <Button
            onClick={handleExportExcel}
            variant="outline"
            size="sm"
            className="h-8 border-[#334155] text-slate-300 hover:bg-[#334155] text-xs"
            data-testid="pippy-export-btn"
          >
            <Download className="w-3.5 h-3.5 mr-1" /> Xuất Excel
          </Button>
        </div>
      </div>

      {/* Summary KPIs */}
      {!loading && data && (
        <div className="grid grid-cols-3 gap-3" data-testid="pippy-summary">
          <div className="bg-gradient-to-br from-red-600/20 to-red-500/5 border border-red-500/20 rounded-xl p-3">
            <p className="text-[9px] text-red-300 uppercase font-bold">Tổng sổ kết vòng</p>
            <p className="text-sm font-bold text-red-400">{fmt(grandSoKetVong)}đ</p>
          </div>
          <div className="bg-gradient-to-br from-sky-600/20 to-sky-500/5 border border-sky-500/20 rounded-xl p-3">
            <p className="text-[9px] text-sky-300 uppercase font-bold">Tổng tiền kiot</p>
            <p className="text-sm font-bold text-sky-300">{fmt(grandKiot)}đ</p>
          </div>
          <div className={`bg-gradient-to-br ${grandLech === 0 ? "from-emerald-600/20 to-emerald-500/5 border-emerald-500/20" : "from-amber-600/20 to-amber-500/5 border-amber-500/20"} border rounded-xl p-3 relative`}>
            <span
              className="absolute top-1.5 right-1.5 text-[8px] uppercase font-bold px-1.5 py-0.5 rounded bg-slate-900/60 text-slate-400 border border-slate-600/40 tracking-wide"
              data-testid="pippy-grand-lech-info-badge"
              title="Hệ thống tự tính = Tổng KiotViet − Tổng Sổ kết vòng. Chỉ tham khảo, không sửa data đã import."
            >
              Tự tính · tham khảo
            </span>
            <p className={`text-[9px] ${grandLech === 0 ? "text-emerald-300" : "text-amber-300"} uppercase font-bold`}>Lệch</p>
            <p className={`text-sm font-bold ${grandLech === 0 ? "text-emerald-400" : grandLech > 0 ? "text-amber-400" : "text-red-400"}`}>
              {grandLech > 0 ? "+" : ""}{fmt(grandLech)}đ
            </p>
          </div>
        </div>
      )}
      {data && (
        <p className="text-[10px] text-slate-500 italic -mt-1" data-testid="pippy-import-note">
          ⓘ Số liệu sổ kết vòng và KiotViet được giữ nguyên 100% từ file đã import — hệ thống không tự sửa.
          KPI "Lệch" chỉ là gợi ý đối chiếu, không ảnh hưởng tới dữ liệu gốc.
        </p>
      )}

      {loading ? (
        <Loader2 className="w-5 h-5 text-emerald-400 animate-spin mx-auto mt-8" />
      ) : groups.length === 0 ? (
        <div className="text-center py-16 bg-[#1e293b] border border-[#334155] rounded-xl">
          <FileSpreadsheet className="w-10 h-10 text-slate-600 mx-auto mb-2" />
          <p className="text-slate-400 text-sm">Chưa có dữ liệu Doanh thu Pippy trong khoảng thời gian này</p>
          <p className="text-slate-600 text-xs mt-1">Upload ảnh bảng kê qua Discord hoặc bấm "Thêm dòng" để nhập tay</p>
        </div>
      ) : (
        <div className="space-y-5" data-testid="pippy-groups">
          {groups.map((g) => (
            <div
              key={g.date}
              className="bg-[#1e293b] border border-[#334155] rounded-xl overflow-hidden"
              data-testid={`pippy-group-${g.date}`}
            >
              {/* Date header */}
              <div className="flex items-center justify-between px-4 py-2 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-b border-[#334155]">
                <div className="flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4 text-amber-400" />
                  <h3 className="text-sm font-bold text-amber-300">Ngày {formatDateVN(g.date)}</h3>
                  <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30 text-[10px]">
                    {g.entries.length} dòng
                  </Badge>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-[#0f172a] border-b border-[#334155]">
                      <th className="text-left p-2 text-slate-400 font-semibold w-[100px]">Ngày</th>
                      <th className="text-left p-2 text-slate-400 font-semibold">Hàng Hoá</th>
                      <th className="text-right p-2 text-slate-400 font-semibold w-[100px]">Số tiền</th>
                      <th className="text-center p-2 text-slate-400 font-semibold w-[70px]">Số Lượng</th>
                      <th className="text-right p-2 text-slate-400 font-semibold w-[120px]">Thành tiền</th>
                      <th className="text-left p-2 text-slate-400 font-semibold w-[200px]">Ghi chú</th>
                      <th className="w-[40px]"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.entries.map((e) => (
                      <tr key={e.id} className="border-b border-[#334155]/50 hover:bg-[#0f172a]/50" data-testid={`pippy-entry-row-${e.id}`}>
                        <td className="p-2 text-slate-300">{formatDateVN(e.date)}</td>
                        <td className="p-2 text-slate-200 font-medium">{e.item_name}</td>
                        <td className="p-2 text-right text-slate-300">{fmt(e.unit_price)}</td>
                        <td className="p-2 text-center text-slate-300">{fmt(e.quantity)}</td>
                        <td className="p-2 text-right text-slate-100 font-semibold">{fmt(e.total)}</td>
                        <td className="p-2 text-slate-500 text-[11px]">{e.note}</td>
                        <td className="p-2 text-right">
                          <button
                            onClick={() => handleDeleteEntry(e.id, e.item_name)}
                            className="text-slate-500 hover:text-red-400 p-1"
                            data-testid={`pippy-delete-${e.id}`}
                            title="Xoá dòng"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </td>
                      </tr>
                    ))}

                    {/* Tổng sổ kết vòng (canonical từ file) */}
                    <tr className="bg-red-500/10 border-t-2 border-red-500/30">
                      <td colSpan={4} className="p-2 text-right font-bold text-red-400">
                        Tổng tiền sổ kết vòng {formatDateVN(g.date)}:
                        {g.has_imported_total && g.subtotal_so_ket_vong_sum !== g.total_so_ket_vong && (
                          <span
                            className="ml-2 inline-flex items-center text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30"
                            title={`Tổng theo file: ${fmt(g.total_so_ket_vong)}đ — SUM dòng: ${fmt(g.subtotal_so_ket_vong_sum)}đ. Hệ thống dùng giá trị file gốc làm canonical.`}
                            data-testid={`pippy-total-mismatch-${g.date}`}
                          >
                            File ≠ Sum
                          </span>
                        )}
                      </td>
                      <td className="p-2 text-right font-bold text-red-400" data-testid={`pippy-subtotal-${g.date}`}>
                        {fmt(g.total_so_ket_vong)}đ
                      </td>
                      <td colSpan={2} className="p-2 text-[10px] text-red-300/60">
                        {g.has_imported_total
                          ? "Từ file"
                          : "SUM entries (chưa có total file)"}
                      </td>
                    </tr>

                    {/* Tổng tiền kiot (editable) */}
                    <tr className="bg-sky-500/10 border-t border-sky-500/30">
                      <td colSpan={4} className="p-2 text-right font-bold text-sky-300">
                        Tổng tiền kiot {formatDateVN(g.date)}:
                      </td>
                      <td className="p-2 text-right">
                        {editingKiot?.date === g.date ? (
                          <div className="flex items-center gap-1 justify-end">
                            <Input
                              type="number"
                              value={editingKiot.total_kiot}
                              onChange={(e) => setEditingKiot({ ...editingKiot, total_kiot: e.target.value })}
                              className="h-7 bg-[#0f172a] border-sky-500/50 text-sky-300 text-xs text-right w-[120px]"
                              data-testid={`pippy-kiot-input-${g.date}`}
                            />
                            <Button
                              onClick={handleSaveKiotTotal}
                              size="sm"
                              className="h-7 w-7 p-0 bg-sky-500 hover:bg-sky-400"
                              data-testid={`pippy-kiot-save-${g.date}`}
                            >
                              <Save className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          <button
                            onClick={() =>
                              setEditingKiot({
                                date: g.date,
                                total_kiot: g.total_kiot || 0,
                                note: g.kiot_note || "",
                              })
                            }
                            className="font-bold text-sky-300 hover:text-sky-200 hover:underline"
                            data-testid={`pippy-kiot-edit-${g.date}`}
                          >
                            {fmt(g.total_kiot)}đ
                          </button>
                        )}
                      </td>
                      <td colSpan={2} className="p-2 text-[10px] text-sky-300/60">
                        {g.total_kiot === 0 && !editingKiot ? "Click số để nhập" : g.kiot_note}
                      </td>
                    </tr>

                    {/* Lệch */}
                    <tr className="bg-amber-500/10 border-t border-amber-500/30">
                      <td colSpan={4} className="p-2 text-right font-bold text-amber-400">
                        Lệch ({formatDateVN(g.date)}):
                      </td>
                      <td
                        className={`p-2 text-right font-bold ${
                          g.lech === 0
                            ? "text-emerald-400"
                            : g.lech > 0
                            ? "text-amber-400"
                            : "text-red-400"
                        }`}
                        data-testid={`pippy-lech-${g.date}`}
                      >
                        {g.lech > 0 ? "+" : ""}{fmt(g.lech)}đ
                      </td>
                      <td colSpan={2} className="p-2 text-[10px] text-amber-400/70">
                        {g.lech === 0 ? "Khớp" : g.lech > 0 ? "Kiot nhiều hơn" : "Sổ kết vòng nhiều hơn"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add entry dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-md" data-testid="pippy-add-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#F8FAFC] text-base">Thêm dòng Doanh thu Pippy</DialogTitle>
            <DialogDescription className="text-slate-500 text-xs">
              Nhập 1 dòng doanh thu theo 5 cột chính + ghi chú.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[11px] text-slate-400 mb-1 block">Ngày</label>
              <Input
                type="date"
                value={newEntry.date}
                onChange={(e) => setNewEntry((p) => ({ ...p, date: e.target.value }))}
                className="h-9 bg-[#0f172a] border-[#334155] text-slate-200 text-xs"
                data-testid="pippy-add-date"
              />
            </div>
            <div>
              <label className="text-[11px] text-slate-400 mb-1 block">Hàng hoá *</label>
              <Input
                value={newEntry.item_name}
                onChange={(e) => setNewEntry((p) => ({ ...p, item_name: e.target.value }))}
                placeholder="VD: Vé trẻ em, DT gà rán, Nước ngọt"
                className="h-9 bg-[#0f172a] border-[#334155] text-slate-200 text-xs"
                data-testid="pippy-add-item"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[11px] text-slate-400 mb-1 block">Số tiền (đơn giá)</label>
                <Input
                  type="number"
                  value={newEntry.unit_price}
                  onChange={(e) => setNewEntry((p) => ({ ...p, unit_price: e.target.value }))}
                  className="h-9 bg-[#0f172a] border-[#334155] text-slate-200 text-xs"
                  data-testid="pippy-add-unit-price"
                />
              </div>
              <div>
                <label className="text-[11px] text-slate-400 mb-1 block">Số lượng</label>
                <Input
                  type="number"
                  value={newEntry.quantity}
                  onChange={(e) => setNewEntry((p) => ({ ...p, quantity: e.target.value }))}
                  className="h-9 bg-[#0f172a] border-[#334155] text-slate-200 text-xs"
                  data-testid="pippy-add-quantity"
                />
              </div>
            </div>
            <div className="text-[11px] text-slate-400 flex items-center gap-2">
              <span>Thành tiền:</span>
              <Input
                type="number"
                value={newEntry.total_overridden ? newEntry.total : ((parseInt(newEntry.unit_price) || 0) * (parseInt(newEntry.quantity) || 0))}
                onChange={(e) => setNewEntry((p) => ({ ...p, total: e.target.value, total_overridden: true }))}
                className="h-8 bg-[#0f172a] border-emerald-500/40 text-emerald-300 text-xs w-[140px] font-semibold"
                data-testid="pippy-add-total"
                title="Nếu file ghi 0 hoặc dấu '-' → bạn nhập 0"
              />
              <span className="text-[10px] text-slate-500 italic">
                {newEntry.total_overridden ? "Bạn đã sửa tay — giữ nguyên giá trị này" : "Auto = giá × SL — sửa nếu khác file"}
              </span>
            </div>
            <div>
              <label className="text-[11px] text-slate-400 mb-1 block">Ghi chú</label>
              <Input
                value={newEntry.note}
                onChange={(e) => setNewEntry((p) => ({ ...p, note: e.target.value }))}
                placeholder="VD: Giảm 100% 2 vé tháng HD184644"
                className="h-9 bg-[#0f172a] border-[#334155] text-slate-200 text-xs"
                data-testid="pippy-add-note"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowAddDialog(false)}
              className="border-[#334155] text-slate-300 hover:bg-[#334155] h-8 text-xs"
            >
              Huỷ
            </Button>
            <Button
              onClick={handleAddEntry}
              className="bg-emerald-500 hover:bg-emerald-400 text-white h-8 text-xs"
              data-testid="pippy-add-submit"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Thêm dòng
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
